'use client';

import { c as _c } from "react/compiler-runtime";
import type { PropsWithChildren } from 'react';
import { ObservableActionFlowProvider, useActionFlow } from '@repo/web3';
import { getPublicClient } from '@wagmi/core';
import { createContext, use, useEffect, useLayoutEffect, useState } from 'react';
import { waitForTransactionReceipt } from 'viem/actions';
import { useConfig } from 'wagmi';
import type { ActionFlowReporter, ActionFlowTracking } from '@/lib/analytics/client/trackers/action-flow';
import { TX_RECEIPT_TIMEOUT } from '@/contexts/Transaction/TransactionV2.context';
import { addTransactionGasBuffer } from '@/helpers/gasBuffer';
import { createActionFlowReporter, getActionFlowTrackingProperties } from '@/lib/analytics/client/trackers/action-flow';
import { getClientLogger, logger } from '@/lib/observability/logger';
import { errorReporter } from '@/lib/observability/logger/error-reporter';
const ActionFlowReporterContext = createContext<ActionFlowReporter | null>(null);
const ActionFlowGateHoldContext = createContext<{
  isHeld: boolean;
  setHeld: (isHeld: boolean) => void;
} | null>(null);
const flushLogger = async () => {
  await logger.flush();
};
const VAULTS_ACTION_FLOW_GAS_POLICY = {
  buffer: addTransactionGasBuffer,
  onRevertedEstimate: 'send'
} as const;

/** The Vaults app wrapper around ObservableActionFlowProvider: Sentry, PostHog and BetterStack adapters, and the legacy checkout-funnel / tx_outcome mapping. */
export function VaultsActionFlowProvider(t0) {
  const $ = _c(17);
  const {
    children
  } = t0;
  const wagmiConfig = useConfig();
  let t1;
  if ($[0] !== wagmiConfig) {
    t1 = () => createActionFlowReporter({
      waitForReceipt: (chainId, hash) => {
        const client = getPublicClient(wagmiConfig, {
          chainId
        });
        if (!client) {
          return Promise.reject(new Error(`No public client for chain ${chainId}`));
        }
        return waitForTransactionReceipt(client, {
          hash,
          timeout: TX_RECEIPT_TIMEOUT
        });
      }
    });
    $[0] = wagmiConfig;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [reporter] = useState(t1);
  const [isGateHolding, setGateHolding] = useState(false);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = getClientLogger();
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== isGateHolding) {
    t3 = {
      isHeld: isGateHolding,
      setHeld: setGateHolding
    };
    $[3] = isGateHolding;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== reporter) {
    t4 = <ActionFlowProgressReporter reporter={reporter} />;
    $[5] = reporter;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== children || $[8] !== t3 || $[9] !== t4) {
    t5 = <ActionFlowGateHoldContext value={t3}>{t4}{children}</ActionFlowGateHoldContext>;
    $[7] = children;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== reporter || $[12] !== t5) {
    t6 = <ActionFlowReporterContext value={reporter}>{t5}</ActionFlowReporterContext>;
    $[11] = reporter;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== reporter || $[15] !== t6) {
    t7 = <ObservableActionFlowProvider errorReporter={errorReporter} eventTracker={reporter} logger={t2.log} onStepSettled={reporter.trackStepSettled} onComplete={flushLogger} gasPolicy={VAULTS_ACTION_FLOW_GAS_POLICY}>{t6}</ObservableActionFlowProvider>;
    $[14] = reporter;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}
function ActionFlowProgressReporter(t0) {
  const $ = _c(12);
  const {
    reporter
  } = t0;
  const {
    state,
    signatureRequests
  } = useActionFlow();
  const signatureCount = signatureRequests?.length ?? 0;
  let t1;
  let t2;
  if ($[0] !== reporter || $[1] !== signatureCount) {
    t1 = () => {
      reporter.setSignatureCount(signatureCount);
    };
    t2 = [reporter, signatureCount];
    $[0] = reporter;
    $[1] = signatureCount;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  const pendingTxHash = state.flowState === "execution" ? state.pendingTxHash : undefined;
  let t3;
  let t4;
  if ($[4] !== pendingTxHash || $[5] !== reporter || $[6] !== state.stepIndex) {
    t3 = () => {
      reporter.reportProgress(state.stepIndex, pendingTxHash);
    };
    t4 = [reporter, state.stepIndex, pendingTxHash];
    $[4] = pendingTxHash;
    $[5] = reporter;
    $[6] = state.stepIndex;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[9] !== reporter) {
    t5 = () => {
      const release = () => reporter.releaseHeldStep();
      window.addEventListener("pagehide", release);
      return () => {
        window.removeEventListener("pagehide", release);
        release();
      };
    };
    t6 = [reporter];
    $[9] = reporter;
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  useEffect(t5, t6);
  return null;
}
export function useHoldActionFlow(isHeld) {
  const $ = _c(4);
  const context = use(ActionFlowGateHoldContext);
  if (!context) {
    throw new Error("useHoldActionFlow must be used within VaultsActionFlowProvider.");
  }
  const {
    setHeld
  } = context;
  let t0;
  let t1;
  if ($[0] !== isHeld || $[1] !== setHeld) {
    t0 = () => {
      setHeld(isHeld);
      return () => setHeld(false);
    };
    t1 = [isHeld, setHeld];
    $[0] = isHeld;
    $[1] = setHeld;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useLayoutEffect(t0, t1);
}
export function useIsActionFlowHeld() {
  const context = use(ActionFlowGateHoldContext);
  if (!context) {
    throw new Error('useIsActionFlowHeld must be used within VaultsActionFlowProvider.');
  }
  return context.isHeld;
}

/** For flows inside VaultsActionFlowProvider: call `setTracking` with a fresh checkout attempt on every Execute click, before the gate simulates; pass `onCancelActiveFlow` to ActionFlowDialog. */
export function useVaultsActionFlowTracking() {
  const $ = _c(6);
  const reporter = use(ActionFlowReporterContext);
  const {
    chainId,
    state,
    stepLabels,
    setTrackingContext
  } = useActionFlow();
  if (!reporter) {
    throw new Error("useVaultsActionFlowTracking must be used within VaultsActionFlowProvider.");
  }
  let t0;
  if ($[0] !== chainId || $[1] !== reporter || $[2] !== setTrackingContext || $[3] !== state || $[4] !== stepLabels) {
    t0 = {
      setTracking(tracking) {
        reporter.setNextTracking(tracking);
        setTrackingContext(getActionFlowTrackingProperties(tracking));
      },
      trackScreeningCancel(chainId_0) {
        reporter.trackScreeningCancel(chainId_0);
      },
      trackSimulationFailure(chainId_1, actionType, error) {
        reporter.trackSimulationFailure(chainId_1, actionType, error);
      },
      onCancelActiveFlow() {
        reporter.trackCancel(state.stepIndex, chainId, stepLabels.length);
      }
    };
    $[0] = chainId;
    $[1] = reporter;
    $[2] = setTrackingContext;
    $[3] = state;
    $[4] = stepLabels;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  return t0;
}